// Brand kit helpers (WS-2.5 dashboard surface). The generate endpoint takes
// raw base64 (no data: prefix) plus palette hexes; these helpers do the
// client-side prep. Pixel math is pure and unit-tested; DOM/File plumbing
// stays thin.

import {
  TV_QUALITY_WARNING_COPY,
  TV_SYMBOL_MAX_ASPECT,
  TV_SYMBOL_MIN_PX,
  TV_TILE_TEXT_MAX,
  TV_BRAND_KIT_COPY,
  type FactoryAssetPlatform,
} from '@/lib/constants/appFactory';
import type {
  FactoryAssetQuality,
  FactoryAssetSet,
  FactoryAssetSetCreateReq,
  FactoryCurrentImage,
} from '@/lib/types/AppFactory';

export interface BrandKitPreset {
  key: string;
  label: string;
  bgColorHex: string;
  textColorHex: string;
}

export class BrandKitUtils {
  static readonly MAX_LOGO_BYTES = 8 * 1024 * 1024;

  static readonly PRESETS: BrandKitPreset[] = [
    { key: 'dark', label: 'Dark', bgColorHex: '#101820', textColorHex: '#ffffff' },
    { key: 'light', label: 'Light', bgColorHex: '#f5f5f2', textColorHex: '#1a1a1a' },
  ];

  /** Strips any data-URL prefix — the API wants raw base64. */
  static toRawBase64(dataUrl: string): string {
    const comma = dataUrl.indexOf(',');
    return comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
  }

  /**
   * Average color of the opaque pixels in RGBA data (the "from logo" accent
   * suggestion). Returns null when nothing is opaque enough to average.
   */
  static averageColorHex(rgba: Uint8ClampedArray): string | null {
    let r = 0;
    let g = 0;
    let b = 0;
    let n = 0;
    for (let i = 0; i + 3 < rgba.length; i += 4) {
      if (rgba[i + 3] < 128) continue; // transparent pixels say nothing about the brand
      r += rgba[i];
      g += rgba[i + 1];
      b += rgba[i + 2];
      n++;
    }
    if (n === 0) return null;
    return BrandKitUtils.rgbToHex(Math.round(r / n), Math.round(g / n), Math.round(b / n));
  }

  static rgbToHex(r: number, g: number, b: number): string {
    const c = (v: number) => Math.max(0, Math.min(255, v)).toString(16).padStart(2, '0');
    return `#${c(r)}${c(g)}${c(b)}`;
  }

  /** Reads a File as raw base64 (data-URL prefix stripped). */
  static async fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(BrandKitUtils.toRawBase64(String(reader.result)));
      reader.onerror = () => reject(new Error('Could not read the file.'));
      reader.readAsDataURL(file);
    });
  }

  /** Reads a File's pixel dimensions; null when undecodable. */
  static async readImageDimensions(file: File): Promise<{ w: number; h: number } | null> {
    const url = URL.createObjectURL(file);
    try {
      return await new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
        img.onerror = () => resolve(null);
        img.src = url;
      });
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  /**
   * PURE slot-pick verdict (vitest-covered): filename/size/dims against a
   * slot spec. dims null = undecodable. An 'exact' slot (the default) must
   * match w x h; a 'max' slot (a bounded mark) must fit within it — the
   * factory adds the trimmed-edges gate. Returns an error message or null.
   */
  static slotFileProblem(
    filename: string,
    sizeBytes: number,
    dims: { w: number; h: number } | null,
    slot: { label: string; w: number; h: number; format: 'png' | 'jpg'; sizeRule?: 'exact' | 'max'; altSizes?: { w: number; h: number }[] },
    maxBytes: number,
  ): string | null {
    const ext = filename.toLowerCase().split('.').pop() ?? '';
    const wantExts = slot.format === 'png' ? ['png'] : ['jpg', 'jpeg'];
    if (!wantExts.includes(ext)) {
      return `${slot.label} must be a ${slot.format.toUpperCase()} file.`;
    }
    if (sizeBytes > maxBytes) {
      return `${slot.label} must be under ${Math.round(maxBytes / 1024 / 1024)}MB.`;
    }
    if (!dims) {
      return `${slot.label} could not be decoded — use a valid ${slot.format.toUpperCase()}.`;
    }
    if (slot.sizeRule === 'max') {
      if (dims.w > slot.w || dims.h > slot.h) {
        return `${slot.label} must be at most ${slot.w} x ${slot.h} pixels (this image is ${dims.w} x ${dims.h}).`;
      }
      return null;
    }
    const sizes = [{ w: slot.w, h: slot.h }, ...(slot.altSizes ?? [])];
    if (!sizes.some((z) => dims.w === z.w && dims.h === z.h)) {
      const want = sizes.map((z) => `${z.w} x ${z.h}`).join(' or ');
      return `${slot.label} must be exactly ${want} pixels (this image is ${dims.w} x ${dims.h}).`;
    }
    return null;
  }

  /**
   * Browser wrapper: validates one manual-set slot pick (fast feedback;
   * the factory re-validates authoritatively).
   */
  static async validateSlotFile(
    file: File,
    slot: { label: string; w: number; h: number; format: 'png' | 'jpg'; sizeRule?: 'exact' | 'max'; altSizes?: { w: number; h: number }[] },
    maxBytes: number,
  ): Promise<string | null> {
    const ext = file.name.toLowerCase().split('.').pop() ?? '';
    const wantExts = slot.format === 'png' ? ['png'] : ['jpg', 'jpeg'];
    // Cheap checks first — skip the decode entirely on wrong type/size.
    if (!wantExts.includes(ext) || file.size > maxBytes) {
      return BrandKitUtils.slotFileProblem(file.name, file.size, null, slot, maxBytes);
    }
    const dims = await BrandKitUtils.readImageDimensions(file);
    return BrandKitUtils.slotFileProblem(file.name, file.size, dims, slot, maxBytes);
  }

  // ── TV graphics v2 (design C1): the Android-family Brand kit ──

  /**
   * The symbol's dominant BRAND colour: opaque pixels bucketed by 4-bit RGB,
   * near-neutral pixels (low saturation, or near black/white) skipped, the
   * fullest bucket's mean returned — the ground-colour suggestion. Null when
   * the mark is all neutral or transparent (the presets stay the choice).
   */
  static dominantBrandColorHex(rgba: Uint8ClampedArray): string | null {
    const buckets = new Map<number, { n: number; r: number; g: number; b: number }>();
    for (let i = 0; i + 3 < rgba.length; i += 4) {
      if (rgba[i + 3] < 128) continue;
      const r = rgba[i];
      const g = rgba[i + 1];
      const b = rgba[i + 2];
      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      if (max < 40 || min > 225) continue; // near black / near white
      if (max === 0 || (max - min) / max < 0.25) continue; // greys
      const key = ((r >> 4) << 8) | ((g >> 4) << 4) | (b >> 4);
      const cur = buckets.get(key) ?? { n: 0, r: 0, g: 0, b: 0 };
      cur.n++;
      cur.r += r;
      cur.g += g;
      cur.b += b;
      buckets.set(key, cur);
    }
    let best: { n: number; r: number; g: number; b: number } | null = null;
    for (const v of buckets.values()) if (!best || v.n > best.n) best = v;
    if (!best) return null;
    return BrandKitUtils.rgbToHex(Math.round(best.r / best.n), Math.round(best.g / best.n), Math.round(best.b / best.n));
  }

  /** True when any pixel is noticeably transparent (a PNG that can sit on the ground colour). */
  static hasTransparency(rgba: Uint8ClampedArray): boolean {
    for (let i = 3; i < rgba.length; i += 4) if (rgba[i] < 250) return true;
    return false;
  }

  /**
   * PURE advisory checks on a picked symbol (never a block — the factory
   * judges authoritatively): too wide for the circle, no transparency, too
   * small for a 4K TV. dims null = undecodable (the pick is refused elsewhere).
   */
  static symbolAdvice(dims: { w: number; h: number } | null, transparent: boolean | null): string[] {
    const out: string[] = [];
    if (!dims) return out;
    const aspect = Math.max(dims.w, dims.h) / Math.max(1, Math.min(dims.w, dims.h));
    if (aspect > TV_SYMBOL_MAX_ASPECT) out.push(TV_BRAND_KIT_COPY.wideSymbolWarning);
    if (transparent === false) out.push(TV_BRAND_KIT_COPY.noTransparency);
    if (Math.max(dims.w, dims.h) < TV_SYMBOL_MIN_PX) out.push(TV_BRAND_KIT_COPY.tooSmallSymbol);
    return out;
  }

  /**
   * PURE mapping of the TV Brand kit inputs to the C1 request (raw base64
   * already read). The app name is trimmed and capped; empty optionals are
   * omitted so an older reader sees no stray empty fields.
   */
  static tvBrandKitRequest(input: {
    symbolBase64: string;
    wordmarkBase64?: string | null;
    tileArtBase64?: string | null;
    tileText?: string | null;
    bgColorHex: string;
    textColorHex?: string | null;
    accentColorHex?: string | null;
  }): FactoryAssetSetCreateReq {
    const req: FactoryAssetSetCreateReq = { symbolBase64: input.symbolBase64, bgColorHex: input.bgColorHex };
    if (input.wordmarkBase64) req.wordmarkBase64 = input.wordmarkBase64;
    if (input.tileArtBase64) req.tileArtBase64 = input.tileArtBase64;
    const text = (input.tileText ?? '').trim().slice(0, TV_TILE_TEXT_MAX).trim();
    if (text) req.tileText = text;
    if (input.textColorHex) req.textColorHex = input.textColorHex;
    if (input.accentColorHex) req.accentColorHex = input.accentColorHex;
    return req;
  }

  /**
   * PURE: the quality verdicts (C1) as customer messages, one per code with
   * the files it concerns, in first-seen order. An unknown code (a newer
   * factory) gets a neutral line naming it.
   */
  static qualityMessages(quality: FactoryAssetQuality[] | undefined | null): { code: string; text: string; files: string[]; known: boolean }[] {
    const byCode = new Map<string, { code: string; text: string; files: string[]; known: boolean }>();
    for (const q of quality ?? []) {
      for (const code of q.warnings ?? []) {
        const known = Object.prototype.hasOwnProperty.call(TV_QUALITY_WARNING_COPY, code);
        const entry = byCode.get(code) ?? {
          code,
          known,
          text: known ? TV_QUALITY_WARNING_COPY[code] : `Something to review on ${q.name}: ${code.replace(/_/g, ' ')}.`,
          files: [],
        };
        if (!entry.files.includes(q.name)) entry.files.push(q.name);
        byCode.set(code, entry);
      }
    }
    return [...byCode.values()];
  }

  /**
   * PURE (design D6): the "your TV tiles can look better" notice shows when
   * the live Android-family set predates composer v2 — the newest approved
   * set says composerVersion absent or 1, or there is no approved set but
   * the app has current images (a console-era set). Never while a draft is
   * pending (the customer is already regenerating). On Roku (design
   * roku_parity R6) only a factory-GENERATED v1 set is nudged: Roku apps
   * often carry designer-made posters (console-era or uploaded), and those
   * are the customer's own choice.
   */
  static needsTvTilesNotice(assetSets: FactoryAssetSet[], currentImages: FactoryCurrentImage[], platform: FactoryAssetPlatform = 'firetv'): boolean {
    if (assetSets.some((s) => s.state === 'generated')) return false;
    const approved = this.approvedByDecision(assetSets)[0];
    if (platform === 'roku') return !!approved && approved.source !== 'manual' && (approved.composerVersion ?? 1) < 2;
    if (approved) return (approved.composerVersion ?? 1) < 2;
    return currentImages.length > 0;
  }

  /**
   * PURE: approved sets, newest decision first. Approved is terminal, so a
   * row's `updated` is the moment it went live — not `created`: a draft made
   * earlier can be approved later and replace a newer set (3.36.1).
   */
  static approvedByDecision(assetSets: FactoryAssetSet[]): FactoryAssetSet[] {
    return assetSets
      .filter((s) => s.state === 'approved')
      .sort((a, b) => (a.updated < b.updated ? 1 : a.updated > b.updated ? -1 : 0));
  }

  /**
   * PURE: the decided sets for the history list, newest decision first, the
   * live set (the newest approval) flagged.
   */
  static historyRows(assetSets: FactoryAssetSet[]): { set: FactoryAssetSet; live: boolean }[] {
    const liveId = this.approvedByDecision(assetSets)[0]?.assetSetId;
    return assetSets
      .filter((s) => s.state !== 'generated')
      .sort((a, b) => (a.updated < b.updated ? 1 : a.updated > b.updated ? -1 : 0))
      .map((set) => ({ set, live: set.assetSetId === liveId }));
  }

  /**
   * PURE: the live set's approval when a pending draft was made BEFORE it —
   * approving that draft would replace newer art. Null otherwise.
   */
  static draftPredatesLive(pending: FactoryAssetSet | undefined, assetSets: FactoryAssetSet[]): FactoryAssetSet | null {
    const live = this.approvedByDecision(assetSets)[0];
    return pending && live && pending.created < live.updated ? live : null;
  }

  /**
   * PURE: Current Images, each URL pointed at the immutable copy inside the
   * approved set that last promoted that file. img/ reuses one name per file
   * and the static CDN keeps a name for up to a day (the objects carry no
   * Cache-Control and query strings are outside its cache key), so the img/
   * URL can keep showing art an approval just replaced; a set's own folder is
   * unique per set and never pruned while approved. Promotion is an overlay
   * (ADR-0017), so a name resolves to the newest approved set holding it; a
   * name no set holds (console-era art) keeps its img/ URL.
   */
  static liveImages(assetSets: FactoryAssetSet[], currentImages: FactoryCurrentImage[]): FactoryCurrentImage[] {
    const approved = this.approvedByDecision(assetSets);
    return currentImages.map((img) => {
      for (const s of approved) {
        const own = s.files?.find((f) => f.name === img.name);
        if (own) return { name: img.name, url: own.url };
      }
      return img;
    });
  }

  /**
   * PURE: the files a TV preview draws, name → URL. A set's own `files` win
   * (a draft previewed before approval); without them only the LIVE set can
   * be drawn, from Current Images. Null when nothing is drawable.
   */
  static previewFileMap(set: FactoryAssetSet | null, currentImages: FactoryCurrentImage[]): Record<string, string> | null {
    const list = set?.files && set.files.length > 0 ? set.files : set && set.state === 'generated' ? [] : currentImages;
    if (list.length === 0) return null;
    return Object.fromEntries(list.map((f) => [f.name, f.url]));
  }

  /**
   * Browser wrapper: decodes a picked image into (downscaled) RGBA for the
   * colour suggestion and the transparency check. Null when undecodable or
   * when the canvas is unavailable (jsdom).
   */
  static async readImagePixels(file: File, maxSide = 256): Promise<{ w: number; h: number; rgba: Uint8ClampedArray } | null> {
    const url = URL.createObjectURL(file);
    try {
      const img = await new Promise<HTMLImageElement | null>((resolve) => {
        const el = new Image();
        el.onload = () => resolve(el);
        el.onerror = () => resolve(null);
        el.src = url;
      });
      if (!img) return null;
      const scale = Math.min(1, maxSide / Math.max(img.naturalWidth, img.naturalHeight));
      const cw = Math.max(1, Math.round(img.naturalWidth * scale));
      const ch = Math.max(1, Math.round(img.naturalHeight * scale));
      const canvas = document.createElement('canvas');
      canvas.width = cw;
      canvas.height = ch;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      ctx.drawImage(img, 0, 0, cw, ch);
      return { w: img.naturalWidth, h: img.naturalHeight, rgba: ctx.getImageData(0, 0, cw, ch).data };
    } catch {
      return null;
    } finally {
      URL.revokeObjectURL(url);
    }
  }
}
